import { useState } from "react";
import { Run } from "./Run";
import { createEntityStore } from "../../common/entityStore";
import { useRxJsSub } from "../../common/utils";
import { Grid } from "@mui/material";
import { propCustomEnumField, propDateField, propNumberField } from "../../common/entityStoreForm";
import { propInt, propMinValue, propRequired, propValidValue } from "../../common/entityStoreValidators";
import { runHubs } from "./RunDetailDialog";
import { RunType } from "./common";
import { createListStore } from "../../common/listStore";
import dayjs from "dayjs";
import { findLast } from "lodash";
import { combineLatest, distinctUntilChanged, map } from "rxjs";

export const ContOrderDeactivationRunPage = () => {
    const [useParamsStore] = useState(() => createEntityStore<RunParams>({
        data: { noShipmentFromDate: "", notesFromDate: "", notesToDate: "", minSkipCount: 6 },
        id: null,
        propValidators: [
            propValidValue("noShipmentFromDate", "noShipmentFromDateFormatted"),
            propValidValue("notesFromDate", "notesFromDateFormatted"),
            propValidValue("notesToDate", "notesToDateFormatted"),
            propRequired("noShipmentFromDate"),
            propRequired("notesFromDate"),
            propRequired("notesToDate"),
            propRequired("skipsFromCycle"),
            propRequired("skipsToCycle"),
            propValidValue("minSkipCount", "minSkipCountFormatted"),
            propRequired("minSkipCount"),
            propInt("minSkipCount"),
            propMinValue("minSkipCount", 0),
        ]
    }));
    const [useCyclesStore] = useState(() => createListStore<CycleDto>({ path: "runs/contOrderDeactivationRun/cycles" }));
    useRxJsSub(() => combineLatest([useCyclesStore.getState().items$, useParamsStore.getState().clientData$.pipe(map(p => p.data.minSkipCount), distinctUntilChanged())]).subscribe({
        next([{ items: cycles }, minSkipCount]) {
            if (minSkipCount != null && minSkipCount >= 0 && cycles.length > 0) {
                const toCycle = findLast(cycles, p => p.isClosed);
                const fromCycle = toCycle == null ? null : cycles.find(p => p.cycleId === toCycle.cycleId - (4 * minSkipCount - 1));
                useParamsStore.getState().setClientDataProp("skipsFromCycle", fromCycle?.cycleId);
                useParamsStore.getState().setClientDataProp("skipsToCycle", toCycle?.cycleId);
                useParamsStore.getState().setClientDataProp("noShipmentFromDate", fromCycle?.startDate ?? "");
                useParamsStore.getState().setClientDataProp("notesFromDate", fromCycle == null ? "" : dayjs(fromCycle.startDate).subtract(3, "days").format("YYYY-MM-DD"));
                useParamsStore.getState().setClientDataProp("notesToDate", toCycle?.startDate ?? "");
            }
        }
    }));
    function renderParams() {
        return <>
            <Grid item xs={12}>{propNumberField(useParamsStore)("minSkipCount", "minSkipCountFormatted", "Minimum skip count (updates \"from\" fields)")}</Grid>
            <Grid item xs={12}>{propDateField(useParamsStore)("noShipmentFromDate", "noShipmentFromDateFormatted", "No shipment from date")}</Grid>
            <Grid item xs={12}>{propDateField(useParamsStore)("notesFromDate", "notesFromDateFormatted", "Notes from date")}</Grid>
            <Grid item xs={12}>{propDateField(useParamsStore)("notesToDate", "notesToDateFormatted", "Notes to date")}</Grid>
            <Grid item xs={12}>{propCustomEnumField(useParamsStore)("skipsFromCycle", "Skips from cycle", useCyclesStore,
                { getId: p => p.cycleId, getName: p => p.name })}</Grid>
            <Grid item xs={12}>{propCustomEnumField(useParamsStore)("skipsToCycle", "Skips to cycle", useCyclesStore,
                { getId: p => p.cycleId, getName: p => p.name })}</Grid>
        </>;
    }
    return <Run name="Order deactivation" hub={runHubs[RunType.ContOrderDeactivation]!} paramsWidth={300}
        useParamsStore={useParamsStore} renderParams={renderParams} />;
};

interface RunParams {
    noShipmentFromDate: string;
    noShipmentFromDateFormatted?: string;
    notesFromDate: string;
    notesFromDateFormatted?: string;
    notesToDate: string;
    notesToDateFormatted?: string;
    skipsFromCycle?: number;
    skipsToCycle?: number;
    minSkipCount: number;
    minSkipCountFormatted?: string;
}

interface CycleDto {
    cycleId: number;
    startDate: string;
    name: string;
    isClosed: boolean;
}
